/**
 * Row prerequisites for the Study Build document rows.
 *
 * Some documents are pure projections over another document and cannot be
 * built until that upstream document exists. Offering Generate anyway means the
 * only way to discover the dependency is to click it and read an error, which
 * is how the Test List behaved: the click failed, and the failure was styled
 * red as though something had broken. Nothing had — the user simply had not
 * generated the upstream document yet.
 *
 * A prerequisite is therefore evaluated up front, from a Firestore document the
 * row names, and reported as a calm note instead of an error.
 *
 * Kept as a pure function, separate from the React row, for the same reason as
 * features/admin/adminAccess.ts: the decision is the part worth testing, and
 * `web` has no test runner of its own (these run under the `functions` package's
 * vitest — see prerequisite.test.ts alongside this file).
 *
 * WORDING. The note text deliberately matches the backend's `PrerequisiteError`
 * strings in functions/src/testList/testListGenerator.ts verbatim, so a user
 * gated up front and a user who somehow reaches the error are told the same
 * thing. If those backend strings change, change these with them.
 */

import { GENERATION_LOCK_STALE_MS } from '../../../../functions/src/utils/generationWindow';

/** The lock window, shared with the backend. */
export const STALE_GENERATION_MS = GENERATION_LOCK_STALE_MS;

/**
 * `info` — a standing condition the user must act on.
 * `progress` — something is already running; the user need only wait.
 */
export type PrerequisiteTone = 'info' | 'progress';

export interface PrerequisiteNote {
  tone: PrerequisiteTone;
  text: string;
}

export interface PrerequisiteState {
  /** True when the row's upstream document can support a generation. */
  met: boolean;
  /** What to show in place of the buttons while unmet; null once met. */
  note: PrerequisiteNote | null;
}

/**
 * A row's prerequisite: which document to watch, and how to read it.
 *
 * `evaluate` receives the raw document data (null when absent) so a row can
 * decide readiness however it needs, without this module knowing about any
 * particular document shape.
 */
export interface RowPrerequisite {
  /** Firestore path, no leading slash — same convention as `firestorePath`. */
  path: (projectId: string) => string;
  evaluate: (data: unknown | null, exists: boolean, now: number) => PrerequisiteState;
}

/**
 * Verbatim from testListGenerator.ts.
 *
 * The document is named in full every time. "Test Procedure" on its own names
 * nothing the user can see: the Study Build row is labelled "Software Systems
 * Testing Procedure", and that is the thing they have to go and generate.
 */
export const TEST_PROCEDURE_MISSING_NOTE =
  'Software Systems Testing Procedure document not found. Generate it first. The Test List is derived from its test cases.';

/** Verbatim from functions/src/testList/testListMessages.ts. */
export const TEST_PROCEDURE_NO_CASES_NOTE =
  'No test cases found in the Software Systems Testing Procedure. Generate the Software Systems Testing Procedure first.';

/**
 * The mid-write state, and the reason this gate exists at all.
 *
 * The procedure writes its test cases in batches and only declares itself
 * finished once the last one lands. Until then the row must not offer Generate:
 * a Test List built from a half-written procedure is silently incomplete rather
 * than failed. Byte-identical to the backend's refusal
 * (TEST_PROCEDURE_STILL_WRITING_MESSAGE) so both routes read the same.
 */
export const TEST_PROCEDURE_GENERATING_NOTE =
  'The Software Systems Testing Procedure is still being written. Try again shortly.';

/** The slice of the Test Procedure document this decision reads. */
export interface TestProcedureStatus {
  testCaseCount?: number;
  _generating?: boolean;
  _generationStartedAt?: { toDate?: () => Date } | null;
}

const MET: PrerequisiteState = { met: true, note: null };

/**
 * Whether the Test List can be generated yet.
 *
 * Order matters. An in-flight run is checked FIRST and beats the case count,
 * because a regeneration replaces every test case — the count still on the
 * document belongs to the previous run and would otherwise read as ready.
 *
 * A `_generating` marker older than the staleness window is ignored: the
 * backend writes that marker up front and a crashed run never clears it, so
 * honouring it forever would gate the row permanently.
 */
export function evaluateTestProcedurePrerequisite(
  data: TestProcedureStatus | null,
  exists: boolean,
  now: number,
): PrerequisiteState {
  const generating = (() => {
    if (!data?._generating) return false;
    const startedAt = data._generationStartedAt;
    if (!startedAt?.toDate) return true; // marker with no start time — assume live
    return now - startedAt.toDate().getTime() < STALE_GENERATION_MS;
  })();

  if (generating) {
    return { met: false, note: { tone: 'progress', text: TEST_PROCEDURE_GENERATING_NOTE } };
  }
  if (!exists || !data) {
    return { met: false, note: { tone: 'info', text: TEST_PROCEDURE_MISSING_NOTE } };
  }
  if ((data.testCaseCount ?? 0) <= 0) {
    return { met: false, note: { tone: 'info', text: TEST_PROCEDURE_NO_CASES_NOTE } };
  }
  return MET;
}

/** The Test List's prerequisite, ready to attach to its row definition. */
export const TEST_LIST_PREREQUISITE: RowPrerequisite = {
  path: (projectId) => `projects/${projectId}/requirements/testProcedure`,
  evaluate: (data, exists, now) =>
    evaluateTestProcedurePrerequisite(data as TestProcedureStatus | null, exists, now),
};
